/**
 * Editor для Прийняття чужої сировини на переробку (Порція 4b).
 * Patterns як у ProductionServiceActEditor.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs as refsApi } from '@/api/refs';
import { accounts as accountsApi } from '@/api/documents';
import DatePopover from '@/components/DatePopover';
import {
  materialReceiptsForProcessing,
  type MaterialReceiptItemIn,
} from '@/api/materialReceiptsForProcessing';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

const PERMISSION = 'production.material_receipt.manage';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  customer_contractor_id: string;
  contract_id: string;
  destination_warehouse_id: string;
  comment: string;
  items: MaterialReceiptItemIn[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  customer_contractor_id: '',
  contract_id: '',
  destination_warehouse_id: '',
  comment: '',
  items: [],
};

const newRow = (n: number): MaterialReceiptItemIn => ({
  line_number: n,
  nomenclature_id: '',
  quantity: '0',
  declared_value: '0',
});

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

export default function MaterialReceiptForProcessingEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, totalAccessibleOrgs } = usePermissionsByOrg(PERMISSION);
  const cQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 1000 }),
  });
  const nomQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.list({ limit: 500 }),
  });

  const accountsQ = useQuery({
    queryKey: ['accounts-postable'],
    queryFn: () => accountsApi.list({ only_postable: true, limit: 1000 }),
  });
  const acctList = accountsQ.data ?? [];

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refsApi.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });

  const docQuery = useQuery({
    queryKey: ['material-receipt-for-processing', id],
    queryFn: () => materialReceiptsForProcessing.get(id!),
    enabled: !isNew,
  });

  const numberPreview = useNumberPreview(
    'doc.material_receipt_for_processing',
    form.organization_id,
    isNew,
  );

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        customer_contractor_id: d.customer_contractor_id,
        contract_id: d.contract_id ?? '',
        destination_warehouse_id: d.destination_warehouse_id,
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          quantity: it.quantity,
          declared_value: it.declared_value ?? '0',
          account_code: it.account_code ?? null,
        })),
      });
    }
  }, [isNew, docQuery.data]);

  const isPosted = docQuery.data?.posted ?? false;

  const acctSelect = (
    value: string | null | undefined,
    onChange: (v: string | null) => void,
    def: string,
  ) => (
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}
            disabled={isPosted} style={{ width: '100%', minWidth: 130 }}>
      <option value="">— (деф. {def})</option>
      {acctList.map((a) => (<option key={a.id} value={a.code}>{a.code} — {a.name}</option>))}
    </select>
  );

  const totalDeclared = useMemo(
    () =>
      form.items.reduce((acc, it) => acc + (Number(it.declared_value) || 0), 0),
    [form.items],
  );

  const create = useMutation({
    mutationFn: () =>
      materialReceiptsForProcessing.create({
        organization_id: form.organization_id,
        document_number: form.document_number || null,
        document_date: new Date(form.document_date).toISOString(),
        customer_contractor_id: form.customer_contractor_id,
        contract_id: form.contract_id || null,
        destination_warehouse_id: form.destination_warehouse_id,
        comment: form.comment || null,
        items: form.items,
      }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['material-receipts-for-processing'] });
      navigate(`/material-receipts-for-processing/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });

  const update = useMutation({
    mutationFn: () =>
      materialReceiptsForProcessing.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        customer_contractor_id: form.customer_contractor_id,
        contract_id: form.contract_id || null,
        destination_warehouse_id: form.destination_warehouse_id,
        comment: form.comment || null,
        items: form.items,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-receipt-for-processing', id] });
      qc.invalidateQueries({ queryKey: ['material-receipts-for-processing'] });
    },
    onError: (e) => setError(extractError(e)),
  });

  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await materialReceiptsForProcessing.create({
        organization_id: form.organization_id,
        document_number: form.document_number || null,
        document_date: new Date(form.document_date).toISOString(),
        customer_contractor_id: form.customer_contractor_id,
        contract_id: form.contract_id || null,
        destination_warehouse_id: form.destination_warehouse_id,
        comment: form.comment || null,
        items: form.items,
      });
      await materialReceiptsForProcessing.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['material-receipts-for-processing'] });
      navigate(`/material-receipts-for-processing/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });

  const updateAndPost = useMutation({
    mutationFn: async () => {
      await materialReceiptsForProcessing.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        customer_contractor_id: form.customer_contractor_id,
        contract_id: form.contract_id || null,
        destination_warehouse_id: form.destination_warehouse_id,
        comment: form.comment || null,
        items: form.items,
      });
      await materialReceiptsForProcessing.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-receipt-for-processing', id] });
      qc.invalidateQueries({ queryKey: ['material-receipts-for-processing'] });
    },
    onError: (e) => setError(extractError(e)),
  });

  const unpost = useMutation({
    mutationFn: () => materialReceiptsForProcessing.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-receipt-for-processing', id] });
      qc.invalidateQueries({ queryKey: ['material-receipts-for-processing'] });
    },
    onError: (e) => setError(extractError(e)),
  });

  function addItem() {
    setForm((f) => ({ ...f, items: [...f.items, newRow(f.items.length + 1)] }));
  }
  function removeItem(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items
        .filter((_, idx) => idx !== i)
        .map((it, idx) => ({ ...it, line_number: idx + 1 })),
    }));
  }
  function setItemField(i: number, patch: Partial<MaterialReceiptItemIn>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)),
    }));
  }

  if (!isNew && docQuery.isLoading) return <div>Завантаження…</div>;

  return (
    <div>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нове прийняття чужої сировини' : `Прийняття: ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ Проведено
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/material-receipts-for-processing')}
          onSave={() => (isNew ? create.mutate() : update.mutate())}
          saveDisabled={create.isPending || update.isPending}
          savePending={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => (isNew ? createAndPost.mutate() : updateAndPost.mutate())}
          postPending={createAndPost.isPending || updateAndPost.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          deletable={false}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
        <div className="field">
          <label>Юрособа</label>
          <select
            value={form.organization_id}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.code} — {o.name}
              </option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>
        <div className="field">
          <label>Номер</label>
          <input
            value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
            placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
            disabled={isPosted}
          />
        </div>
        <div className="field">
          <label>Дата</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={isPosted}
          />
        </div>
        <div className="field">
          <label>Замовник (власник сировини)</label>
          <select
            value={form.customer_contractor_id}
            onChange={(e) => setForm({ ...form, customer_contractor_id: e.target.value })}
            disabled={isPosted}
          >
            <option value="">—</option>
            {cQ.data?.items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Склад призначення</label>
          <select
            value={form.destination_warehouse_id}
            onChange={(e) =>
              setForm({ ...form, destination_warehouse_id: e.target.value })
            }
            disabled={isPosted}
          >
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (
              <option key={w.id} value={w.id}>
                {w.code} — {w.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Коментар</label>
          <input
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            disabled={isPosted}
          />
        </div>
      </div>

      <div style={{ marginTop: '1rem', fontWeight: 600 }}>
        Чужа сировина — задекларована вартість: {totalDeclared.toFixed(2)}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 40 }}>#</th>
            <th style={{ padding: '0.5rem' }}>Сировина</th>
            <th style={{ padding: '0.5rem', width: 100 }}>К-сть</th>
            <th style={{ padding: '0.5rem', width: 150 }}>Декл. вартість</th>
            <th style={{ padding: '0.5rem', width: 150 }}>Рах. обліку</th>
            <th style={{ padding: '0.5rem', width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
              <td style={{ padding: '0.5rem' }}>
                <select
                  value={it.nomenclature_id}
                  onChange={(e) => setItemField(i, { nomenclature_id: e.target.value })}
                  disabled={isPosted}
                >
                  <option value="">—</option>
                  {nomQ.data?.items.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.code} — {n.name}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input
                  type="number"
                  step="0.001"
                  value={it.quantity}
                  onChange={(e) => setItemField(i, { quantity: e.target.value })}
                  disabled={isPosted}
                />
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input
                  type="number"
                  step="0.01"
                  value={it.declared_value ?? '0'}
                  onChange={(e) => setItemField(i, { declared_value: e.target.value })}
                  disabled={isPosted}
                />
              </td>
              <td style={{ padding: '0.5rem' }}>
                {acctSelect(it.account_code, (v) => setItemField(i, { account_code: v }), '022')}
              </td>
              <td style={{ padding: '0.5rem' }}>
                {!isPosted && (
                  <button onClick={() => removeItem(i)} title="Видалити">
                    ×
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!isPosted && (
        <button onClick={addItem} style={{ marginTop: '0.5rem' }}>
          + Додати рядок
        </button>
      )}

      {error && (
        <div className="error" style={{ marginTop: '1rem' }}>
          {error}
        </div>
      )}
    </div>
  );
}
